/**
 * LivePlatformStats — public stats strip for the /how-it-works page.
 *
 * Fetches `GET /api/resume-studio/platform-stats` and animates counting
 * up from 0 to the returned figure on scroll-into-view. Graceful
 * degradation: renders nothing if the endpoint fails so the trust page
 * never breaks.
 */
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
    Sparkle, TrendUp, ClockClockwise, FileText, ChartLineUp,
} from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;

function useCountUp(target, durationMs = 1400, active = true) {
    const [value, setValue] = useState(0);
    useEffect(() => {
        if (!active || typeof target !== "number") return undefined;
        let raf;
        const start = performance.now();
        const from = 0;
        const to = target;
        const tick = (t) => {
            const elapsed = t - start;
            const p = Math.min(1, elapsed / durationMs);
            // easeOutCubic
            const eased = 1 - Math.pow(1 - p, 3);
            setValue(from + (to - from) * eased);
            if (p < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [target, durationMs, active]);
    return value;
}

function StatCard({ icon: Icon, value, suffix, label, sublabel, testId, active, decimals = 0 }) {
    const animated = useCountUp(value, 1400, active);
    const displayNum = decimals > 0
        ? animated.toFixed(decimals)
        : Math.round(animated).toLocaleString("en-IN");
    return (
        <div
            data-testid={testId}
            className="relative border border-slate-200 bg-white rounded-2xl p-5 md:p-6 overflow-hidden group hover:border-cobalt-300 transition-colors"
        >
            <div className="absolute -right-4 -top-4 w-20 h-20 rounded-full bg-cobalt-50/60 group-hover:bg-cobalt-100/70 transition-colors" aria-hidden />
            <div className="relative">
                <div className="w-9 h-9 rounded-lg bg-cobalt-600 text-white grid place-items-center mb-3">
                    <Icon weight="fill" size={18} />
                </div>
                <div className="text-3xl md:text-4xl font-black tabular-nums leading-none text-slate-900">
                    {displayNum}
                    {suffix ? <span className="text-cobalt-600 ml-0.5">{suffix}</span> : null}
                </div>
                <div className="mt-2 text-sm font-semibold text-slate-800">{label}</div>
                {sublabel ? (
                    <div className="text-xs text-slate-500 mt-0.5">{sublabel}</div>
                ) : null}
            </div>
        </div>
    );
}

export default function LivePlatformStats() {
    const [stats, setStats] = useState(null);
    const [error, setError] = useState(false);
    const [inView, setInView] = useState(false);
    const rootRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await axios.get(`${API}/api/resume-studio/platform-stats`);
                if (!cancelled) setStats(data);
            } catch (_e) {
                if (!cancelled) setError(true);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (!rootRef.current) return undefined;
        const obs = new IntersectionObserver(
            (entries) => {
                entries.forEach((e) => {
                    if (e.isIntersecting) setInView(true);
                });
            },
            { threshold: 0.2 },
        );
        obs.observe(rootRef.current);
        return () => obs.disconnect();
    }, [stats]);

    if (error) return null;

    // Skeleton while loading
    if (!stats) {
        return (
            <section
                ref={rootRef}
                data-testid="platform-stats-loading"
                className="pb-16"
            >
                <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3">
                    Live platform stats
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[0, 1, 2, 3].map((i) => (
                        <div
                            key={i}
                            className="border border-slate-200 bg-white rounded-2xl p-6 animate-pulse"
                        >
                            <div className="w-9 h-9 rounded-lg bg-slate-200 mb-3" />
                            <div className="h-8 bg-slate-200 rounded w-24 mb-2" />
                            <div className="h-3 bg-slate-100 rounded w-32" />
                        </div>
                    ))}
                </div>
            </section>
        );
    }

    return (
        <section
            ref={rootRef}
            data-testid="platform-stats-live"
            className="pb-16"
        >
            <div className="flex items-center gap-2 mb-3">
                <div className="text-xs font-bold text-slate-500 uppercase tracking-widest">
                    Live platform stats
                </div>
                <div className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full text-[10px] font-bold border border-emerald-100">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    LIVE
                </div>
            </div>
            <h2 className="text-2xl font-bold mb-6">
                Real numbers. Updated every 5 minutes.
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard
                    icon={FileText}
                    value={stats.cvs_generated}
                    label="CVs delivered"
                    sublabel="Paid rewrites shipped"
                    testId="stat-cvs-generated"
                    active={inView}
                />
                <StatCard
                    icon={Sparkle}
                    value={stats.cvs_rated}
                    label="CVs rated free"
                    sublabel="Rate-My-CV sessions"
                    testId="stat-cvs-rated"
                    active={inView}
                />
                <StatCard
                    icon={TrendUp}
                    value={stats.avg_ats_lift}
                    suffix="pts"
                    label="Avg ATS lift"
                    sublabel="Before → After delta"
                    testId="stat-ats-lift"
                    active={inView}
                    decimals={1}
                />
                <StatCard
                    icon={ClockClockwise}
                    value={stats.hours_saved}
                    label="Hours saved"
                    sublabel="~3 hrs per CV rewrite"
                    testId="stat-hours-saved"
                    active={inView}
                />
            </div>
            <div className="mt-4 flex items-center gap-2 text-xs text-slate-500">
                <ChartLineUp size={12} />
                <span>
                    Avg post-rewrite ATS score:&nbsp;
                    <b className="text-slate-700 tabular-nums">
                        {Number(stats.avg_ats_after).toFixed(1)}
                    </b>
                    &nbsp;/ 100
                </span>
            </div>
        </section>
    );
}
